import { useAppContext } from "../AppContextDef";
import useAuthFetch from "./useAuthFetch";

export const logType = {
  ERROR: "ERROR",
  NAVIGATION: "NAVIGATION",
  ACTION: "ACTION",
  START: "START",
  END: "END",
  // One row per answered face-verification API call. Written only by the
  // recorder, including for calls it delegated to the client.
  IDENTITY: "IDENTITY",
};


export const useJourney = () => {
  const customFetch = useAuthFetch();
  const {
    deviceData,
    allDetails,
    journeyData,
    applicationNo,
    allPubsubData,
    endApiCalledRef
  } = useAppContext();

  // The recorder never runs Index's fetchAllDetails (it is skipped for
  // #/recording), so allDetails / applicationNo / journeyData are all null
  // there. It receives the same values over the ALL_DETAILS channel instead, so
  // fall back to those — without this, every log written from the recorder is
  // rejected for a null APPLICATION_NO and never reaches the journey.
  const authToken = allDetails?.authToken ?? allPubsubData?.authToken;
  const logApplicationNo = applicationNo ?? allPubsubData?.applicationNo;
  const logJourneyId = journeyData?.journeyId ?? allPubsubData?.journeyId;

  const logJourney = async ({ type, message, metadata }) => {
    const url = `${process.env.REACT_APP_API_URL}/v1/log/create`;
    const method = "POST";
    const headers = {
      authorization: authToken,
      "Content-Type": "application/json",
    };
    const bodyContent = {
      applicationNo: logApplicationNo,
      journeyId: logJourneyId,
      logType: type,
      logMessage: message,
      metadata,
    };
    try {
      return await customFetch(url, method, headers, bodyContent);
    } catch (error) {
      console.error("Error logging journey:", error);
    }
  };

  const endJourney = async ({ journeyStatus, disagreeReason, customerImage }) => {
    try {
      endApiCalledRef.current = true

      const url = `${process.env.REACT_APP_API_URL}/v1/journey/end`;
      const method = "POST";
      const headers = {
        authorization: allDetails?.authToken,
        "Content-Type": "application/json",
      };
      const bodyContent = {
        journeyId: journeyData?.journeyId,
        journeyStatus,
        disagreeReason,
        customerImage,
        lat: deviceData?.latitude,
        lng: deviceData?.longitude,
        deviceDetails: {
          deviceName: deviceData?.deviceName,
          browserName: deviceData?.browserName,
          network: deviceData?.network,
        },
      };
      const { status } = await customFetch(url, method, headers, bodyContent);
      return status
    } catch (error) {
      console.error("Error ending journey:", error);
      return null;
    }
  };

  const updateJourney = async ({ screenshots, journeyQna, customerImage }) => {
    const url = `${process.env.REACT_APP_API_URL}/v1/journey/update`;
    const method = "POST";
    const headers = {
      authorization: allDetails?.authToken,
      "Content-Type": "application/json",
    };
    const bodyContent = {
      journeyId: journeyData?.journeyId,
      lat: deviceData?.latitude,
      lng: deviceData?.longitude,
      deviceDetails: {
        deviceName: deviceData?.deviceName,
        browserName: deviceData?.browserName,
        network: deviceData?.network,
      },
      journeyScreenshots: screenshots,
      journeyQna: journeyQna,
      customerImage,
    };
    
    try {
      return await customFetch(url, method, headers, bodyContent);
    } catch (error) {
      console.error("Error updating journey:", error);
    }
  };

  return { logJourney, endJourney, updateJourney };
};

// One stack for every language. The browser picks a font per glyph, so mixed
// scripts in a single string (Hindi + English, Tamil + English, ...) all render.
// Keep this in sync with --font-app in index.css.
export const APP_FONT_STACK = [
  '"Mulish"',
  '"Noto Sans"',
  '"Noto Sans Devanagari"',
  '"Noto Sans Bengali"',
  '"Noto Sans Gujarati"',
  '"Noto Sans Gurmukhi"',
  '"Noto Sans Kannada"',
  '"Noto Sans Malayalam"',
  '"Noto Sans Oriya"',
  '"Noto Sans Tamil"',
  '"Noto Sans Telugu"',
  "Arial",
  "sans-serif",
].join(", ");

